import React, { useRef } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";
import { UserSocialSignOn } from "../services/v2/socialSignOns";
import { updateUserDetailsToStorage } from "../helpers/authHelpers";
import { getLocalStorage, setLocalStorage, removeLocalStorage, getUserType } from "../helpers/common";
import { convertToNumber } from "../helpers/validations";
import { USER_INFO_KEY, REAL_ESTATE_AGENT } from "../constants/common";
import { pageURLs } from "@/constants";
import { apiCall, apiCallWithoutToken } from "../services/v2/apiService";
import * as endPoints from "../constants/v2/endPoints";
import { triggerCrossLogin, isAllowedRedirectUrl } from "../helpers/crossLoginHelper";
import { saveUserType_Title, saveAgentInfo } from "../services/v2/signupService";
import { createSubscriptionPlanOnSignup } from "../services/PlanService";
import { getUserDetails } from "../services/userprofileService";
import { isCurrentTenantMlsRestricted } from "../helpers/tenant";

const isProfileComplete = (user) => {
  return user?.UserStatus === true || user?.UserStatus === "true";
};

// BLS tenants skip the legacy profile-completion flow; everyone else completes it.
const redirectAfterFailedOnboarding = () => {
  window.location.replace(
    isCurrentTenantMlsRestricted()
      ? pageURLs.home
      : pageURLs.completeYourProfileDefault,
  );
};

const completeSSOOnboarding = async (userId) => {
  const ud = getLocalStorage(USER_INFO_KEY);
  if (!ud?.userInfo) return false;

  if (!ud.userInfo.agentTitle) {
    const titleRes = await saveUserType_Title({ title: REAL_ESTATE_AGENT });
    if (_.get(titleRes, "errorCode", "") !== 0) {
      return false;
    }
    ud.userInfo.agentTitle = convertToNumber(REAL_ESTATE_AGENT);
    setLocalStorage(USER_INFO_KEY, ud);
  }

  if (!ud.userInfo.planStatus) {
    const planRes = await createSubscriptionPlanOnSignup({
      PlanName: "Lite",
      PlanPeriod: 1,
      UserType: getUserType(),
    });
    if (planRes?.errorCode === 0) {
      ud.userInfo.planStatus = true;
      setLocalStorage(USER_INFO_KEY, ud);
    }
  }

  if (!ud.userInfo.userStatus) {
    const detailsRes = await getUserDetails({ id: userId });
    if (detailsRes?.errorCode === 0 && detailsRes?.result) {
      const details = Array.isArray(detailsRes.result) ? detailsRes.result[0] : detailsRes.result;
      const resolvedLocation = details?.Location || ud?.userInfo?.location || "";
      const fieldValues = {
        FirstName: details?.FirstName,
        LastName: details?.LastName,
        Phone: details?.Phone,
        Company: details?.Company,
        LicenseNo: details?.LicenseNo,
        Location: resolvedLocation,
      };

      const allFilled = Object.values(fieldValues).every((v) => (v ?? "").toString().trim());

      if (!allFilled) {
        return false;
      }

      const saveRes = await saveAgentInfo({
        FirstName: details.FirstName,
        LastName: details.LastName,
        Company: details.Company,
        Location: resolvedLocation,
        LicenseNo: details.LicenseNo,
        Phone: details.Phone,
      });
      if (saveRes?.errorCode === 0) {
        ud.userInfo.userStatus = true;
        ud.userInfo.firstName = details.FirstName;
        ud.userInfo.company = details.Company;
        ud.userInfo.lastName = details.LastName;
        ud.userInfo.name = details.FirstName + " " + details.LastName;
        ud.userInfo.location = resolvedLocation;
        setLocalStorage(USER_INFO_KEY, ud);
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  return true;
};

const SSOCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const paramsRef = useRef(null);


  if (!paramsRef.current) {
    paramsRef.current = {
      code: searchParams.get("code"),
      redirect: searchParams.get("redirect"),
      ssoError: searchParams.get("error"),
      ssoStatus: searchParams.get("status"),
      pendingToken: searchParams.get("pendingToken"),
      maskedEmail: searchParams.get("email"),
      stateParam: searchParams.get("state"),
      flow: searchParams.get("flow"),
    };
  }

  const { code, redirect, ssoError, ssoStatus, pendingToken, maskedEmail, stateParam, flow } = paramsRef.current;

  const isSAMLFlow = flow === "saml" && code;
  const isOIDCFlow = code && flow !== "saml";
  const isPendingVerification = ssoStatus === "pending_verification" && pendingToken;
  const isBlocked = ssoError === "non_agent_account" || ssoError === "agent_not_found";

  const blockedMessages = {
    agent_not_found: "We could not find your member record. Please contact support.",
    non_agent_account: "This email is already associated with a non-agent account. Please contact support or use a different email to sign in.",
  };

  if (hasProcessed.current) {
    return <LoadingSpinner />;
  }

  if (isBlocked) {
    hasProcessed.current = true;
    return (
      <Navigate
        to={pageURLs.home}
        replace
        state={{ ssoError: blockedMessages[ssoError] || blockedMessages.non_agent_account }}
      />
    );
  }

  if (isPendingVerification && !hasProcessed.current) {
    hasProcessed.current = true;
    return (
      <Navigate
        to={pageURLs.home}
        replace
        state={{
          pendingMlsLink: true,
          pendingToken,
          maskedEmail: maskedEmail || "",
          redirect,
        }}
      />
    );
  }

  const handleSAMLFlow = () => {
    hasProcessed.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    apiCallWithoutToken(endPoints.SAML_EXCHANGE, "POST", { code })
      .then(async (exchangeRes) => {
        const data = exchangeRes?.data;
        const samlToken = data?.userSession;
        const samlRefreshToken = data?.refreshToken;
        const samlRedirect = data?.redirect;

        if (!samlToken) {
          toast.error("Sign-in failed. Please try again.");
          setTimeout(() => navigate(pageURLs.home), 3000);
          return;
        }

        const meRes = await apiCall(endPoints.AUTH_ME, "GET", null, null, samlToken);
        const user = meRes?.result?.user;
        if (!user) {
          toast.error("Sign-in failed. Please try again.");
          setTimeout(() => navigate(pageURLs.home), 3000);
          return;
        }

        updateUserDetailsToStorage(
          {
            result: {
              user,
              userSession: samlToken,
              refreshToken: samlRefreshToken,
              tutorialStatus: meRes?.result?.tutorialStatus,
            },
          },
          "saml"
        );

        const finalRedir = samlRedirect || redirect;
        const isValidRedirect = finalRedir && (
          (finalRedir.startsWith("/") && !finalRedir.startsWith("//")) ||
          isAllowedRedirectUrl(finalRedir)
        );
        const finalRedirect = isValidRedirect ? finalRedir : pageURLs.home;

        const stored = getLocalStorage(USER_INFO_KEY);
        const needsOnboarding = !isProfileComplete(user)
          || !stored?.userInfo?.agentTitle
          || !stored?.userInfo?.planStatus;
        if (needsOnboarding) {
          const profileComplete = await completeSSOOnboarding(user?.UserId);
          if (!profileComplete) {
            redirectAfterFailedOnboarding();
            return;
          }
        }

        const returnUrl = window.location.origin + finalRedirect;
        const didCrossLogin = await triggerCrossLogin(returnUrl);
        if (!didCrossLogin) {
          window.location.replace(finalRedirect);
        }
      })
      .catch((err) => {
        removeLocalStorage(USER_INFO_KEY);
        toast.error("Sign-in failed. Please try again.");
        setTimeout(() => navigate(pageURLs.home), 3000);
      });
  };

  const handleOIDCFlow = () => {
    hasProcessed.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    if (!stateParam) {
      toast.error("Sign-in failed. Missing state parameter.");
      setTimeout(() => navigate(pageURLs.home), 3000);
      return;
    }

    UserSocialSignOn({ code, state: stateParam })
      .then(async (response) => {
        if (response?.errorCode) {
          toast.error(response.message || "Sign-in failed. Please try again.");
          setTimeout(() => navigate(pageURLs.home), 3000);
          return;
        }

        const result = response?.result;

        if (result?.status === "pending_verification") {
          navigate(pageURLs.home, {
            replace: true,
            state: {
              pendingMlsLink: true,
              pendingToken: result.pendingToken,
              maskedEmail: result.maskedEmail || "",
            },
          });
          return;
        }

        const provider = response?.result?.provider || null;
        updateUserDetailsToStorage(response, provider);

        const user = response?.result?.user;
        const storedOidc = getLocalStorage(USER_INFO_KEY);
        const needsOnboarding = !isProfileComplete(user)
          || !storedOidc?.userInfo?.agentTitle
          || !storedOidc?.userInfo?.planStatus;

        if (needsOnboarding) {
          const profileComplete = await completeSSOOnboarding(user?.UserId);
          if (!profileComplete) {
            redirectAfterFailedOnboarding();
            return;
          }
        }

        const returnUrl = window.location.origin + pageURLs.home;
        const didCrossLogin = await triggerCrossLogin(returnUrl);
        if (!didCrossLogin) {
          window.location.replace(pageURLs.home);
        }
      })
      .catch((err) => {
        const message = err?.response?.data?.message || "Sign-in failed. Please try again.";
        toast.error(message);
        setTimeout(() => navigate(pageURLs.home), 3000);
      });
  };

  if (isSAMLFlow) {
    handleSAMLFlow();
  }

  if (isOIDCFlow) {
    handleOIDCFlow();
  }

  if (!isSAMLFlow && !isOIDCFlow) {
    toast.error("Invalid SSO callback. Missing required parameters.");
    return <Navigate to={pageURLs.home} replace />;
  }

  return <LoadingSpinner />;
};

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center vh-100">
    <div className="text-center">
      <div className="spinner-border" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3">Signing you in...</p>
    </div>
  </div>
);

export default SSOCallback;
